import { createCareer, deleteCareer, getCareerList, getCareerListForOwner, reorderCareer, updateCareerBulk, updateCareerVisibility } from '../../api/careerApi';
import { useCareerListStore } from '../../store/careerStore';
import type { ResourceScope, ResourceStatus } from '../../util/scopedResource';
import { CareerData } from '../../dto/EntityRepository';
import { CreateCareerReq, RemoveCareerReq, UpdateCareerBulkReq, UpdateCareerVisibilityReq, UpdateReorderListReq } from '../../dto/ReqDtoRepository';
import { CareerListResponse, CareerResponse } from '../../dto/ResDtoRepository';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { useApiWrapper } from './apiWrapper';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { normalizeCareer, normalizeCareerList } from '../../util/normalizeCareer';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';
import { useModalStore } from '../../store/portal/modalStore';


interface UseCareerStateResult {
  careerList: CareerData[];
  listOwnerAui: string | null;
  listScope: ResourceScope | null;
  status: ResourceStatus;
}

interface UseCareerActionsResult {
  getCareerList: () => Promise<void>;
  getCareerListOwner: () => Promise<void>;
  createCareer: (data: CreateCareerReq) => Promise<void>;
  updateCareerBulk: (data: UpdateCareerBulkReq) => Promise<void>;
  updateCareerVisibility: (data: UpdateCareerVisibilityReq) => Promise<void>;
  deleteCareer: (data: RemoveCareerReq) => Promise<void>;
  reorderCareer: (data: UpdateReorderListReq) => Promise<void>;
}

export const useCareerState = (): UseCareerStateResult => {
  const careerList = useCareerListStore((state) => state.careers);
  const listOwnerAui = useCareerListStore((state) => state.listOwnerAui);
  const listScope = useCareerListStore((state) => state.listScope);
  const status = useCareerListStore((state) => state.status);

  return { careerList, listOwnerAui, listScope, status };
};

export const useCareerActions = (): UseCareerActionsResult => {
  const pageOwnerAui = usePageOwnerIdentityStore((state) => state.pageOwner?.aui);
  const setCareers = useCareerListStore((state) => state.setCareers);
  const beginCareerListFetch = useCareerListStore((state) => state.beginCareerListFetch);
  const setCareerList = useCareerListStore((state) => state.setCareerList);
  const setCareerListError = useCareerListStore((state) => state.setCareerListError);
  const afterCreateCareer = useCareerListStore((state) => state.afterCreateCareer);
  const markCareersSaved = useCareerListStore((state) => state.markCareersSaved);
  const afterDeleteCareer = useCareerListStore((state) => state.afterDeleteCareer);
  const setOnlyAccessValue = useCareerListStore((state) => state.setOnlyAccessValue);
  const { setUpdatedTempAlert, setDeletedTempAlert } = useTempAlertStore();
  const withApiHandler = useApiWrapper();

  const handleGetCareerListSuccess = (
    response: CareerListResponse,
    ownerAui: string,
    scope: ResourceScope,
  ) => {
    setCareerList(normalizeCareerList(response.data), ownerAui, scope);
  };
  const handleCreateCareerSuccess = (response: CareerResponse) => {
    afterCreateCareer(normalizeCareer(response.data));
    useModalStore.getState().clearModal(400);
  };
  const handleUpdateCareerBulkSuccess = (request: UpdateCareerBulkReq) => {
    markCareersSaved(request.careerList.map(({ careerId }) => careerId));
    setUpdatedTempAlert();
  };
  const handleUpdateCareerVisibilitySuccess = (response: CareerResponse) => {
    const updatedCareerData = normalizeCareer(response.data);
    setOnlyAccessValue(updatedCareerData.id, updatedCareerData.isPublic);
    setUpdatedTempAlert();
  };
  const handleDeleteCareerSuccess = (careerId: string) => {
    afterDeleteCareer(careerId);
    console.log("deleted well");
    setDeletedTempAlert();
  };
  const handleReorderCareerSuccess = (response: CareerListResponse) => {
    setCareers(normalizeCareerList(response.data));
    setUpdatedTempAlert();
  };

  const fetchCareerList = async (aui: string, scope: ResourceScope) => {
    beginCareerListFetch(aui, scope);
    try {
      return await runCurrentArchiveRequest(
        aui,
        () => (scope === 'owner' ? getCareerListForOwner(aui) : getCareerList(aui)),
        (response) => handleGetCareerListSuccess(response, aui, scope),
      );
    } catch (error) {
      setCareerListError();
      throw error;
    }
  };

  const handleCareerRequest = async (
    action: 'get' | 'get_for_owner' | 'create' | 'update_bulk' | 'update_visibility' | 'delete' | 'reorder',
    data?: CreateCareerReq | UpdateCareerVisibilityReq | UpdateCareerBulkReq | RemoveCareerReq | UpdateReorderListReq
  ) => {
    if (!pageOwnerAui) return;
    const aui = pageOwnerAui;
    const apiFunction = async () => {
      switch (action) {
        case 'create':
          return runCurrentArchiveRequest(
            aui,
            () => createCareer(aui, data as CreateCareerReq),
            handleCreateCareerSuccess,
          );
        case 'update_bulk': {
          const request = data as UpdateCareerBulkReq;
          return runCurrentArchiveRequest(
            aui,
            () => updateCareerBulk(aui, request),
            () => handleUpdateCareerBulkSuccess(request),
          );
        }
        case 'update_visibility':
          return runCurrentArchiveRequest(
            aui,
            () => updateCareerVisibility(aui, data as UpdateCareerVisibilityReq),
            handleUpdateCareerVisibilitySuccess,
          );
        case 'delete': {
          const careerId = (data as RemoveCareerReq).careerId;
          return runCurrentArchiveRequest(
            aui,
            () => deleteCareer(aui, data as RemoveCareerReq),
            () => handleDeleteCareerSuccess(careerId),
          );
        }
        case 'reorder':
          return runCurrentArchiveRequest(
            aui,
            () => reorderCareer(aui, data as UpdateReorderListReq),
            handleReorderCareerSuccess,
          );
        case 'get_for_owner':
          return fetchCareerList(aui, 'owner');
        case 'get':
        default:
          return fetchCareerList(aui, 'public');
      }
    };

    await withApiHandler(apiFunction, [aui, action, data]);
  };

  return {
    getCareerList: () => handleCareerRequest('get'),
    getCareerListOwner: () => handleCareerRequest('get_for_owner'),
    createCareer: (data: CreateCareerReq) => handleCareerRequest('create', data),
    updateCareerBulk: (data: UpdateCareerBulkReq) => handleCareerRequest('update_bulk', data),
    updateCareerVisibility: (data: UpdateCareerVisibilityReq) => handleCareerRequest('update_visibility', data),
    deleteCareer: (data: RemoveCareerReq) => handleCareerRequest('delete', data),
    reorderCareer: (data: UpdateReorderListReq) => handleCareerRequest('reorder', data),
  };
};

/** 기존 호출부 호환: state + actions */
export const useCareer = (): UseCareerStateResult & UseCareerActionsResult => ({
  ...useCareerState(),
  ...useCareerActions(),
});
